import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { INTL_DAY_MONTH_NAME_SHORT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import moment from 'moment'
import { rem } from 'polished'
import { forwardRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type { TripDay } from 'tripPlanner/api/types'
import { STOP_TIMING_STICKY_HEADER_Z_INDEX } from './stopTimingLayers'

interface HeaderStop {
  id: number
  placeName: string
  days: Array<TripDay>
}

interface Props {
  stops: Array<HeaderStop>
  selectedDayId?: number
  onDayClick: (dayId: number) => void
}

interface StopGroupProps {
  stop: HeaderStop
  selectedDayId?: number
  onDayClick: (dayId: number) => void
}

interface DayButtonProps {
  day: TripDay
  selected: boolean
  onDayClick: (dayId: number) => void
}

const HeaderContainer = styled.div`
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: ${STOP_TIMING_STICKY_HEADER_Z_INDEX};
  display: flex;
  align-items: center;
  height: ${rem(73)};
  width: 100%;
  padding: 0 var(--itinerary-canvas-padding);
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: 0 ${rem(10)} ${rem(14)} ${rem(-14)} rgba(20, 24, 35, 0.18);
  scrollbar-color: ${props => props.theme.palette.neutral.surface.lightForeground} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: ${rem(4)};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${props => props.theme.borderRadius.round};
    background: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const HeaderTrack = styled.div`
  display: flex;
  flex: 0 0 auto;
  margin: 0 auto;
`

const StopGroup = styled.div`
  display: grid;
  grid-template-columns: max-content max-content;
  min-height: ${rem(72)};

  & + & {
    border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const StopName = styled(BodyText)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${rem(69)};
  height: ${rem(72)};
  padding: ${rem(16)} ${rem(18)};
  box-sizing: border-box;
`

const DayList = styled.div`
  display: flex;
  gap: ${rem(8)};
  height: ${rem(72)};
  padding-block: ${rem(8)};
  padding-inline: ${rem(8)} ${rem(12)};
  box-sizing: border-box;
`

const DateButton = styled(Clickable)<{ $selected: boolean }>`
  display: grid;
  min-width: ${rem(55)};
  height: ${rem(56)};
  align-items: center;
  justify-items: center;
  gap: ${rem(2)};
  border: 1px solid transparent;
  border-radius: ${rem(12)};
  padding: ${rem(8)};
  background: ${props => props.$selected ? props.theme.palette.neutral.default.six : props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  transition: background 120ms ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${props => props.theme.palette.neutral.default.six};
    }
  }

  /*
   * Rebrand replaces the grey selected/hover fills with the sand surface ramp:
   * selected sits on Light Surface ALT, hover on the lighter Light Surface so
   * the two states stay distinguishable from each other and from the page.
   */
  body.${REBRAND_PALETTE_CLASS} & {
    background: ${props => props.$selected ? props.theme.palette.neutral.surface.lightBackgroundAlt : props.theme.palette.neutral.surface.normalBackground};

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${props => props.$selected ? props.theme.palette.neutral.surface.lightBackgroundAlt : props.theme.palette.neutral.surface.lightBackground};
      }
    }
  }
`

function getDateValue(date: string): Date | undefined {
  const parsedDate = moment(date, ISO_DATE_FORMAT, true)
  if (parsedDate.isValid()) return parsedDate.toDate()

  const nativeDate = new Date(date)
  return Number.isNaN(nativeDate.getTime()) ? undefined : nativeDate
}

function ChatItineraryDateHeaderDay({ day, selected, onDayClick }: DayButtonProps) {
  const intl = useIntl()

  const handleClick = useCallback(() => {
    onDayClick(day.id)
  }, [day.id, onDayClick])

  const dateValue = day.date ? getDateValue(day.date) : undefined
  const weekdayLabel = dateValue ? intlDateFormatter(dateValue, { weekday: 'short' }, intl.locale) : undefined
  const dateLabel = dateValue ? intlDateFormatter(dateValue, INTL_DAY_MONTH_NAME_SHORT, intl.locale) : intl.formatMessage(
    {
      id: 'nKq1zQQctl_T9caEJO6m',
      defaultMessage: 'Day {dayNumber}',
      description: 'Label for a specific day number in the trip planner itinerary',
    },
    { dayNumber: day.dayNumber },
  )

  return (
    <DateButton $selected={selected} aria-pressed={selected} onClick={handleClick}>
      {weekdayLabel && (
        <Caption variant="small" colour="neutral-three">
          {weekdayLabel}
        </Caption>
      )}
      <BodyText variant="medium" weight="normal" wrap="no-wrap">
        {dateLabel}
      </BodyText>
    </DateButton>
  )
}

function ChatItineraryDateHeaderStop({ stop, selectedDayId, onDayClick }: StopGroupProps) {
  return (
    <StopGroup>
      <StopName variant="medium" weight="bold" wrap="no-wrap">
        {stop.placeName}
      </StopName>
      <DayList>
        {stop.days.map(day => (
          <ChatItineraryDateHeaderDay
            key={day.id}
            day={day}
            selected={selectedDayId === day.id}
            onDayClick={onDayClick}
          />
        ))}
      </DayList>
    </StopGroup>
  )
}

const ChatItineraryDateHeader = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { stops, selectedDayId, onDayClick } = props

  if (stops.length === 0) return null

  return (
    <HeaderContainer ref={ref}>
      <HeaderTrack>
        {stops.map(stop => (
          <ChatItineraryDateHeaderStop
            key={stop.id}
            stop={stop}
            selectedDayId={selectedDayId}
            onDayClick={onDayClick}
          />
        ))}
      </HeaderTrack>
    </HeaderContainer>
  )
})

ChatItineraryDateHeader.displayName = 'ChatItineraryDateHeader'

export default ChatItineraryDateHeader
